import PropTypes from "prop-types"
import { columnsPropTypes, rowsPropTypes } from "./tablePropsTypes"
import TableRow from "components/ui/TableRow"
import TableRowHeader from "components/ui/TableRowHeader"
import TableLoading from "components/ui/TableLoading"
import SearchInput from "components/ui/SearchInput"
import { Overflowing } from "components/ui"

const BaseTable = ({
  isDraggable = false,
  draggableType = null,
  providedPlaceholder = null,
  columns = [],
  rows = [],
  isLoading,
  className = null,
  style = {},
  hasTableHeader = true,
  emptyTableMessage = null,
  emptyTableImageUrl = null,
  onRowClick = null,
  onSortClick = null,
  sortDir,
  sortBy,
  isSelectable = false,
  selectableField,
  onSelectChange,
  isDeletable,
  onDeleteClick,
  searchValue,
  onSearchChange = null,
  onSearchClear = null,
  searchPlacement,
  overflowWrapperStyle = {},
  searchInputStyle = {}
}) => {
  const handleSort = async (_event, sortBy) => {
    await onSortClick({ sortBy })
  }

  const handleSelectableChange = (row, e) => {
    onSelectChange && onSelectChange(row, sortBy, sortDir, e)
  }

  const handleInputChange = async ({ target: { value } }) =>
    await onSearchChange({ searchValue: value })

  const footerRow = rows.find(r => r.id === "footer")
  const bodyRows = rows.filter(r => r.id !== "footer")

  return (
    <Overflowing className="Overflowing" style={overflowWrapperStyle}>
      {onSearchChange && searchPlacement == "aboveTable" && (
        <div>
          <SearchInput
            placeholder="Search by name"
            value={searchValue}
            onChange={handleInputChange}
            onClear={onSearchClear}
            style={searchInputStyle}
          />
        </div>
      )}
      <table className={className} style={style}>
        {hasTableHeader && (
          <TableRowHeader
            columns={columns}
            isDraggable={isDraggable}
            isSelectable={isSelectable}
            onSort={handleSort}
            sortBy={sortBy}
            sortDir={sortDir}
          />
        )}
        <tbody>
          {isLoading && <TableLoading columnCount={columns.length} />}
          {!isLoading && bodyRows.length === 0 && emptyTableMessage && (
            <tr>
              <td className="ta-c" colSpan={isDraggable ? columns.length + 1 : columns.length}>
                <div className="ta-c p-4">
                  <div className="blank-state__hero">
                    {emptyTableImageUrl && (
                      <img src={emptyTableImageUrl} className="mb-4" alt="No results" />
                    )}
                  </div>
                  <div className="blank-state__msg blank-state__msg--wide">{emptyTableMessage}</div>
                </div>
              </td>
            </tr>
          )}
          {!isLoading &&
            bodyRows.map((row, index) => (
              <TableRow
                key={row.key || row.id}
                columns={columns}
                row={row}
                index={index}
                isDraggable={isDraggable}
                draggableType={draggableType}
                onRowClick={onRowClick}
                className={row.className}
                style={row.style}
                isSelectable={isSelectable}
                onSelectChange={handleSelectableChange}
                selectableField={selectableField}
                isDeletable={isDeletable}
                onDeleteClick={onDeleteClick}
              />
            ))}
          {providedPlaceholder}
        </tbody>
        {footerRow && (
          <tfoot>
            <tr>{footerRow.render && footerRow.render(footerRow, columns)}</tr>
          </tfoot>
        )}
      </table>
    </Overflowing>
  )
}

BaseTable.propTypes = {
  isDraggable: PropTypes.bool,
  draggableType: PropTypes.string,
  providedPlaceholder: PropTypes.object,
  columns: columnsPropTypes,
  rows: rowsPropTypes,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  hasTableHeader: PropTypes.bool,
  emptyTableMessage: PropTypes.string,
  emptyTableImageUrl: PropTypes.string,
  onRowClick: PropTypes.func,
  onSortClick: PropTypes.func,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  isSelectable: PropTypes.bool,
  selectableField: PropTypes.string,
  onSelectChange: PropTypes.func,
  isDeletable: PropTypes.bool,
  onDeleteClick: PropTypes.func,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchClear: PropTypes.func,
  searchPlacement: PropTypes.string,
  children: PropTypes.node,
  overflowWrapperStyle: PropTypes.object,
  searchInputStyle: PropTypes.object
}

export default BaseTable
